import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LaunchEncoder } from './LaunchEncoder';
import {
  api, formatUsd, type Template, type TemplateOption, type ResourceGroup, type LaunchWait, ApiError,
} from '../lib/api';
import { LaunchWaits, CapacityLine, useNow } from '../components/LaunchWaits';
import { Button, Card, Field, Input, TextArea, Select, Alert, Spinner } from '../components/ui';


/** What POST /templates/launch takes. */
interface LaunchBody {
  templateId: string;
  sizeId: string;
  name: string;
  groupId: string | null;
  rootSizeGiB: number;
  options: Record<string, string | boolean>;
  staticIp: boolean;
}

/**
 * Every option's default, including the hidden ones.
 *
 * Hidden options are still sent. They are declared because the API still
 * accepts them and a launch from a bare image still needs them - they are
 * simply not a choice worth putting in front of an operator any more.
 */
function defaultsFor(template: Template): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const o of template.options ?? []) {
    if (o.type === 'generated-secret') continue;   // the server makes these
    if (o.default === undefined || o.default === null) continue;
    out[o.id] = o.default as string | boolean;
  }
  return out;
}

/** What an option is currently warning about, if anything. */
function warningFor(o: TemplateOption, value: string | boolean | undefined): string | null {
  if (o.type === 'boolean' && value === true && o.warnWhenTrue) return o.warnWhenTrue;
  if (o.warnOnValue && typeof value === 'string') return o.warnOnValue[value] ?? null;
  return null;
}

/** Machine or encoder - two different kinds of thing, launched from one place. */
function LaunchKind({ encoder }: { encoder: boolean }) {
  const tab = (active: boolean) =>
    `rounded-md px-3 py-1.5 text-sm ${active ? 'bg-raised text-ink ring-1 ring-hairline' : 'text-ink-secondary hover:text-ink'}`;
  return (
    <div className="flex gap-1">
      <Link to="/launch" className={tab(!encoder)}>Instance</Link>
      <Link to="/launch/encoder" className={tab(encoder)}>MediaLive encoder</Link>
    </div>
  );
}

export function Launch({ encoder = false }: { encoder?: boolean }) {
  if (encoder) {
    return (
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="space-y-3">
          <LaunchKind encoder />
          <div>
            <h1 className="text-lg font-semibold">Launch an encoder</h1>
            <p className="text-sm text-ink-muted">
              A MediaLive channel with its SRT ingest flows and output flows, created and started together.
            </p>
          </div>
        </div>
        <LaunchEncoder />
      </div>
    );
  }
  return <LaunchMachine />;
}

function LaunchMachine() {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [groups, setGroups] = useState<ResourceGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [templateId, setTemplateId] = useState<string>('');
  const [sizeId, setSizeId] = useState<string>('');
  const [name, setName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [rootSizeGiB, setRootSizeGiB] = useState<number>(500);
  // Every template can have one; off by default, because most machines are
  // launched for one event and terminated.
  const [staticIp, setStaticIp] = useState(false);

  // Every option the selected template declares, keyed by id.
  //
  // This form used to hardcode its fields, so a template could declare an
  // option and never be asked about it. The multiviewer declares the grid size
  // and whether to record; neither was ever shown, so every multiviewer
  // launched 2x2 with recording off no matter what the operator wanted, and
  // there was nowhere to look for the setting because there was nothing there.
  const [optionValues, setOptionValues] =
    useState<Record<string, string | boolean>>({});

  // Launches waiting for AWS capacity. `watching` is the one this page started,
  // which it follows to the machine page once it launches. What each was
  // launched WITH is kept so "Try again" can send the same thing - held here
  // only, so after a reload "Try again" refills the form instead.
  const [waits, setWaits] = useState<LaunchWait[]>([]);
  const [watching, setWatching] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const sent = useRef(new Map<string, LaunchBody>());
  const sizeCard = useRef<HTMLDivElement>(null);
  const launchCard = useRef<HTMLDivElement>(null);
  const anyWaiting = waits.some((w) => w.status === 'waiting');
  const now = useNow(true);

  useEffect(() => {
    async function load() {
      try {
        const [t, g] = await Promise.all([
          api.get<{ templates: Template[] }>('/templates'),
          api.get<{ groups: ResourceGroup[] }>('/admin/groups').catch(() => ({ groups: [] })),
        ]);

        setTemplates(t.templates);
        setGroups(g.groups);

        const first = t.templates[0];
        if (first) {
          setTemplateId(first.id);
          setSizeId(first.sizes.find((s) => s.default)?.id ?? first.sizes[0]?.id ?? '');
          setRootSizeGiB(first.storage.root.defaultSizeGiB);
          setOptionValues(defaultsFor(first));
        }
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not load templates.');
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, []);

  // Waits started elsewhere - another tab, or before a reload - show up too.
  // Polled quickly only while one is in progress.
  useEffect(() => {
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    async function poll() {
      try {
        const res = await api.get<{ waits: LaunchWait[] }>('/templates/launch-waits');
        if (stop) return;
        setWaits(res.waits);
        if (watching) {
          const mine = res.waits.find((w) => w.id === watching);
          if (mine?.status === 'launched' && mine.instanceId) {
            navigate(`/machines/${mine.instanceId}`);
            return;
          }
          if (!mine) {
            // Waits live in the dashboard's memory; a restart loses them.
            setWatching(null);
            setError('The dashboard restarted and stopped waiting for capacity. Launch again.');
          }
        }
      } catch { /* keep the last view; try again next tick */ }
      if (!stop) timer = setTimeout(poll, anyWaiting || watching ? 2000 : 15000);
    }
    void poll();
    return () => { stop = true; clearTimeout(timer); };
  }, [watching, anyWaiting, navigate]);

  // The capacity readout on each size comes with the template list, so refresh
  // that now and then - more often while a wait is producing new sightings.
  useEffect(() => {
    const t = setInterval(() => {
      api.get<{ templates: Template[] }>('/templates').then((r) => setTemplates(r.templates)).catch(() => {});
    }, anyWaiting ? 20_000 : 60_000);
    return () => clearInterval(t);
  }, [anyWaiting]);

  const template = templates.find((t) => t.id === templateId);
  const size = template?.sizes.find((s) => s.id === sizeId);

  // Options belong to a template, so switching template starts them again from
  // that template's defaults rather than carrying the last one's answers over.
  function chooseTemplate(next: Template): void {
    setTemplateId(next.id);
    setSizeId(next.sizes.find((x) => x.default)?.id ?? next.sizes[0]?.id ?? '');
    setRootSizeGiB(next.storage.root.defaultSizeGiB);
    setOptionValues(defaultsFor(next));
  }

  const visibleOptions = (template?.options ?? []).filter(
    (o) => !o.hidden && ['select', 'boolean', 'secret', 'text', 'textarea'].includes(o.type),
  );

  const hourly = size?.livePricePerHour ?? null;
  const eventDay = hourly ? hourly * 8 : null;
  const fullDay = hourly ? hourly * 24 : null;

  async function onLaunch(): Promise<void> {
    if (!template || !size || !name.trim()) return;
    await submit({
      templateId: template.id,
      sizeId: size.id,
      name: name.trim(),
      groupId: groupId || null,
      rootSizeGiB,
      // Sent exactly as the template declares them, hidden ones included.
      options: optionValues,
      staticIp,
    });
  }

  async function submit(body: LaunchBody): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      const result = await api.post<{ instanceId?: string; waiting?: boolean; wait?: LaunchWait }>(
        '/templates/launch', body,
      );

      if (result.waiting && result.wait) {
        // Every zone was full. Not a failure: the dashboard keeps asking, and
        // this page follows along and moves on when it launches.
        const w = result.wait;
        sent.current.set(w.id, body);
        setWatching(w.id);
        setWaits((prev) => [w, ...prev.filter((x) => x.id !== w.id)]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      navigate(`/machines/${result.instanceId}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Launch failed.');
    } finally {
      setBusy(false);
    }
  }

  async function cancelWait(w: LaunchWait): Promise<void> {
    try {
      await api.delete(`/templates/launch-waits/${w.id}`);
    } catch (err) {
      // Most likely it launched or ran out in the same moment; the next poll
      // shows which.
      if (!(err instanceof ApiError && err.status === 404)) {
        setError(err instanceof ApiError ? err.message : 'Could not stop waiting.');
      }
    }
    if (watching === w.id) setWatching(null);
    setWaits((prev) => prev.map((x) => (x.id === w.id && x.status === 'waiting'
      ? { ...x, status: 'cancelled', message: 'Stopped waiting for capacity', nextAttemptAt: null } : x)));
  }

  // Put the form back to what that launch asked for.
  function refill(w: LaunchWait): void {
    const t = templates.find((x) => x.id === w.templateId);
    if (t && t.id !== templateId) chooseTemplate(t);
    if (t?.sizes.some((x) => x.id === w.sizeId)) setSizeId(w.sizeId);
    setName(w.name);
  }

  function retry(w: LaunchWait): void {
    dismiss(w);
    const body = sent.current.get(w.id);
    if (body) {
      void submit(body);
      return;
    }
    refill(w);
    launchCard.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function pickAnother(w: LaunchWait): void {
    void cancelWait(w);
    dismiss(w);
    refill(w);
    sizeCard.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function setWaitSizes(w: LaunchWait, sizeIds: string[]): Promise<void> {
    // Shown straight away; the server's answer replaces it a moment later.
    setWaits((prev) => prev.map((x) => (x.id === w.id ? { ...x, trying: [x.sizeId, ...sizeIds] } : x)));
    try {
      const res = await api.patch<{ wait: LaunchWait }>(`/templates/launch-waits/${w.id}`, { sizeIds });
      setWaits((prev) => prev.map((x) => (x.id === w.id ? res.wait : x)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the sizes.');
    }
  }

  function dismiss(w: LaunchWait): void {
    setDismissed((prev) => new Set(prev).add(w.id));
  }

  if (loading) return <Spinner label="Loading templates…" />;

  if (templates.length === 0) {
    return <Alert tone="warning">No templates found. Check the templates/ directory.</Alert>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <LaunchKind encoder={false} />
      <div>
        <h1 className="text-lg font-semibold">Launch an instance</h1>
        <p className="text-sm text-ink-muted">
          Pick a template and a size. Everything else has a sensible default.
        </p>
      </div>

      {error && <Alert tone="critical">{error}</Alert>}

      <LaunchWaits
        waits={waits.filter((w) => !dismissed.has(w.id))}
        now={now}
        onCancel={(w) => void cancelWait(w)}
        onRetry={retry}
        onDismiss={dismiss}
        onPickAnother={pickAnother}
        onSizes={(w, sizeIds) => void setWaitSizes(w, sizeIds)}
      />

      {/* --- Template --- */}
      <Card title="Template">
        <div className="space-y-2 p-4">
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => chooseTemplate(t)}
              className={`w-full rounded-md p-3 text-left transition-colors ring-1 ${
                t.id === templateId
                  ? 'bg-raised ring-series-1'
                  : 'ring-hairline hover:bg-raised/50'
              }`}
            >
              <div className="text-sm font-medium">{t.name}</div>
              <div className="mt-0.5 text-xs text-ink-secondary">{t.summary}</div>
              {t.state.default_ami_id && (
                <div className="mt-1 text-xs" style={{ color: 'var(--color-good)' }}>
                  ✓ Using a captured image — boots in ~3 minutes
                </div>
              )}
            </button>
          ))}
        </div>
      </Card>

      {/* --- Size --- */}
      {template && (
        <div ref={sizeCard} className="scroll-mt-4">
        <Card title="Size">
          <div className="space-y-2 p-4">
            {template.sizes.map((s) => {
              const selected = s.id === sizeId;

              return (
                <button
                  key={s.id}
                  onClick={() => setSizeId(s.id)}
                  className={`w-full rounded-md p-3 text-left transition-colors ring-1 ${
                    selected
                      ? 'bg-raised ring-series-1'
                      : 'ring-hairline hover:bg-raised/50'
                  } ${s.deprioritised && !selected ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="text-sm font-medium">
                      {s.label}
                      {s.default && (
                        <span className="ml-2 rounded bg-plane px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-ink-muted">
                          recommended
                        </span>
                      )}
                    </div>
                    <div className="shrink-0 text-sm tabular">
                      {s.livePricePerHour ? `${formatUsd(s.livePricePerHour, 2)}/hr` : '—'}
                    </div>
                  </div>

                  <div className="mt-1 text-xs text-ink-secondary">
                    {s.vcpu} vCPU · {s.memoryGiB} GB RAM · {s.gpu} · {s.instanceType}
                  </div>

                  {s.bestFor && (
                    <div className="mt-1 text-xs text-ink-muted">{s.bestFor}</div>
                  )}

                  <CapacityLine seen={s.capacity} now={now} />

                  {/* Caution is a warning icon plus text, never colour alone. */}
                  {s.caution && (
                    <div className="mt-1.5 flex gap-1.5 text-xs" style={{ color: 'var(--color-warning)' }}>
                      <span aria-hidden>⚠</span>
                      <span>{s.caution}</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </Card>
        </div>
      )}

      {/* --- Details --- */}
      <Card title="Details">
        <div className="grid gap-4 p-4 sm:grid-cols-2">
          <Field
            label="Instance name"
            hint={
              name.trim()
                ? `Will appear in AWS as "cloudprod-${name.trim()}"`
                : 'Automatically prefixed with cloudprod- so it is identifiable in the AWS console.'
            }
          >
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Cricket Match 1 - vMix"
            />
          </Field>

          <Field label="Group" hint="Used for access control and cost reporting.">
            <Select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">No group</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Disk size"
            hint={`About US$${((rootSizeGiB * 0.096) / 30).toFixed(2)}/day while stopped.`}
          >
            <Select
              value={rootSizeGiB}
              onChange={(e) => setRootSizeGiB(Number(e.target.value))}
            >
              {template?.storage.root.sizeGiBOptions.map((gb) => (
                <option key={gb} value={gb}>
                  {gb} GB
                </option>
              ))}
            </Select>
          </Field>

          {/* Not a template option: every machine can have one. */}
          <div className="space-y-2 sm:col-span-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={staticIp} onChange={(e) => setStaticIp(e.target.checked)} />
              Static IP address
            </label>
            <p className="text-xs text-ink-muted">
              Keeps the same public address through every stop and start - so an encoder's SRT
              address, bookmarks and firewall allow-lists keep working between events. Worth it for a
              instance kept for one client and stopped between uses. No extra cost while running (every
              public address costs US$0.005/hr either way); about US$3.60 a month while stopped.
              Released when the instance is terminated.
            </p>
          </div>

          {/* Whatever this template declares, rather than a fixed list. An
              option added to a template.json is asked about here without
              touching this file. Options the template marks hidden are still
              sent at their default - see TemplateOption. */}
          {visibleOptions.map((o) => {
            const value = optionValues[o.id];
            const warning = warningFor(o, value);

            if (o.type === 'boolean') {
              return (
                <div key={o.id} className="space-y-2 sm:col-span-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={value === true}
                      onChange={(e) =>
                        setOptionValues((v) => ({ ...v, [o.id]: e.target.checked }))
                      }
                    />
                    {o.label}
                  </label>
                  {o.description && (
                    <p className="text-xs text-ink-muted">{o.description}</p>
                  )}
                  {warning && (
                    <Alert tone="warning" title={o.label}>
                      {warning}
                    </Alert>
                  )}
                </div>
              );
            }

            // Free text - a Live Channel's channel name, or its list of users,
            // one per line. Neither ever reaches user-data: templates that take
            // these declare launchConfig and get them through SSM.
            if (o.type === 'text' || o.type === 'textarea') {
              return (
                <div key={o.id} className="sm:col-span-2">
                  <Field label={o.label} hint={o.description}>
                    {o.type === 'textarea' ? (
                      <TextArea
                        rows={4}
                        spellCheck={false}
                        placeholder={o.placeholder}
                        className="font-mono"
                        value={String(value ?? '')}
                        onChange={(e) =>
                          setOptionValues((v) => ({ ...v, [o.id]: e.target.value }))
                        }
                      />
                    ) : (
                      <Input
                        type="text"
                        autoComplete="off"
                        placeholder={o.placeholder}
                        value={String(value ?? '')}
                        onChange={(e) =>
                          setOptionValues((v) => ({ ...v, [o.id]: e.target.value }))
                        }
                      />
                    )}
                  </Field>
                  {o.note && <p className="mt-1 text-xs text-ink-muted">{o.note}</p>}
                </div>
              );
            }

            // A value typed at launch - the multiviewer's public view password.
            // Shown in the clear while typing, because it is one the operator
            // is about to hand to other people, not one they have to keep.
            if (o.type === 'secret') {
              return (
                <div key={o.id} className="sm:col-span-2">
                  <Field label={o.label} hint={o.description}>
                    <Input
                      type="text"
                      autoComplete="off"
                      spellCheck={false}
                      placeholder={o.placeholder}
                      value={String(value ?? '')}
                      onChange={(e) =>
                        setOptionValues((v) => ({ ...v, [o.id]: e.target.value }))
                      }
                    />
                  </Field>
                  {o.note && String(value ?? '').trim() && (
                    <p className="mt-1 text-xs text-ink-muted">{o.note}</p>
                  )}
                </div>
              );
            }

            return (
              <Field key={o.id} label={o.label} hint={o.description}>
                <Select
                  value={String(value ?? '')}
                  onChange={(e) =>
                    setOptionValues((v) => ({ ...v, [o.id]: e.target.value }))
                  }
                >
                  {(o.choices ?? []).map((c) => (
                    <option key={String(c)} value={String(c)}>
                      {o.choiceLabels?.[String(c)] ?? String(c)}
                    </option>
                  ))}
                </Select>
                {warning && (
                  <Alert tone="warning" title={o.label}>
                    {warning}
                  </Alert>
                )}
              </Field>
            );
          })}
        </div>
      </Card>

      {/* Cost stated before the button, not after. Nobody should be surprised
          by what they just committed to. */}
      {optionValues.autoShutdownIdleMinutes === '0' && (
        <Alert tone="warning" title="Auto-shutdown is off">
          This instance will bill continuously until someone stops it — about{' '}
          {formatUsd(fullDay)} per day. An instance forgotten over a long weekend costs more than
          most events make. You can turn it on later from the instance page.
        </Alert>
      )}

      <div ref={launchCard}>
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4 p-4">
          <div className="text-sm">
            <div className="text-ink-secondary">Estimated cost</div>
            <div className="mt-1 flex gap-4 tabular">
              <span>
                <strong>{formatUsd(hourly, 2)}</strong>
                <span className="text-ink-muted">/hr</span>
              </span>
              <span className="text-ink-secondary">
                {formatUsd(eventDay)} per 8-hour event day
              </span>
            </div>
          </div>

          <Button
            variant="primary"
            loading={busy}
            disabled={!name.trim() || !size}
            onClick={() => void onLaunch()}
          >
            Launch instance
          </Button>
        </div>
      </Card>
      </div>

      {template && (
        <p className="text-center text-xs text-ink-muted">
          Provisioning takes about {template.provisioning.expectedDurationMinutes} minutes from a
          base image, then the instance reboots.
        </p>
      )}
    </div>
  );
}
